import type { ComputedRef, Ref } from 'vue';
import { computed, ref, shallowRef } from 'vue';
import type { DSBPortfolioItemResponseDTO, DSBStrategyItemResponseDTO } from '@/api-v2/web/discover';
import type { DataDiscoveryUniverse } from '@/api-v2/web/discover/types/DataDiscoveryUniverse';

/**
 * Lookup that supports the Map.get / Map.has API used across benchmark resolution,
 * without cloning strategy + equity universes into one physical Map (that clone was the
 * Discover → Analytics jank on hotfix/WAA-16122).
 */
export type StrategyLookupMap = {
  get: (key: string) => DSBStrategyItemResponseDTO | undefined;
  has: (key: string) => boolean;
};

/**
 * @deprecated - should use 'useAllStrategies' from useDataDiscovery.ts
 */
const allStrategies = shallowRef<readonly DSBStrategyItemResponseDTO[]>([]);

/**
 * @deprecated - should use 'useAllPortfolios' from useDataDiscovery.ts
 */
const allPortfolios = shallowRef<readonly DSBPortfolioItemResponseDTO[]>([]);

/**
 * @deprecated - should use 'useAllStrategiesByCode' from useDataDiscovery.ts
 */
const allStrategiesByCode = shallowRef<Map<string, DSBStrategyItemResponseDTO>>(
  new Map<string, DSBStrategyItemResponseDTO>(),
);

/**
 * @deprecated - should use 'useAllStrategiesById' from useDataDiscovery.ts
 */
const allStrategiesById = shallowRef<Map<string, DSBStrategyItemResponseDTO>>(
  new Map<string, DSBStrategyItemResponseDTO>(),
);

/**
 * @deprecated - should use 'useAllPortfoliosById' from useDataDiscovery.ts
 */
const allPortfoliosById = shallowRef<Map<string, DSBPortfolioItemResponseDTO>>(
  new Map<string, DSBPortfolioItemResponseDTO>(),
);

/**
 * @deprecated - should use 'useAllPortfoliosBySlug' from useDataDiscovery.ts
 */
const allPortfoliosBySlug = shallowRef<Map<string, DSBPortfolioItemResponseDTO>>(
  new Map<string, DSBPortfolioItemResponseDTO>(),
);

const allEquitiesByCode = shallowRef<Map<string, DSBStrategyItemResponseDTO>>(
  new Map<string, DSBStrategyItemResponseDTO>(),
);

const allEquitiesById = shallowRef<Map<string, DSBStrategyItemResponseDTO>>(
  new Map<string, DSBStrategyItemResponseDTO>(),
);

/**
 * Strategies + equities by code. Use this instead of `allStrategiesByCode` when resolving
 * benchmarks / tracks that may be stocks.
 */
const allTracksByCode = computed((): StrategyLookupMap => {
  const strategies = allStrategiesByCode.value;
  const equities = allEquitiesByCode.value;
  return {
    get: (key) => strategies.get(key) ?? equities.get(key),
    has: (key) => strategies.has(key) || equities.has(key),
  };
});

/**
 * Strategies + equities by id. Use this instead of `allStrategiesById` when resolving
 * benchmarks / tracks that may be stocks.
 */
const allTracksById = computed((): StrategyLookupMap => {
  const strategies = allStrategiesById.value;
  const equities = allEquitiesById.value;
  return {
    get: (key) => strategies.get(key) ?? equities.get(key),
    has: (key) => strategies.has(key) || equities.has(key),
  };
});

function _setStrategyUniverse(strategies: DSBStrategyItemResponseDTO[]): void {
  allStrategies.value = strategies;

  const strategiesByCode: [string, DSBStrategyItemResponseDTO][] = strategies.map((o) => [o.code, o]);
  allStrategiesByCode.value = new Map(strategiesByCode);

  const strategiesById: [string, DSBStrategyItemResponseDTO][] = strategies.map((o) => [o.id, o]);
  allStrategiesById.value = new Map(strategiesById);
}

/**
 * Portfolios created from live strategy holdings (shouldExposePositions = true) are not supposed to be shown as a portfolio
 * But they have portfolio tree structures so that they can access Portfolio construction
 * Basket rebalancing, Risk, Factor Decomposition and Position Page
 * We have to filter them out here
 */
function _setPortfolioUniverse(portfoliosRaw: DSBPortfolioItemResponseDTO[]): void {
  const portfolios = portfoliosRaw.filter((o) => !o.shouldExposePositions);

  allPortfolios.value = Object.freeze(portfolios);

  const portfoliosById: [string, DSBPortfolioItemResponseDTO][] = portfolios.map((o) => [o.portfolioId, o]);
  allPortfoliosById.value = Object.freeze(new Map(portfoliosById));

  const portfoliosBySlug: [string, DSBPortfolioItemResponseDTO][] = portfolios.map((o) => [o.slug, o]);
  allPortfoliosBySlug.value = Object.freeze(new Map(portfoliosBySlug));
}

function setUniverse(universe: DataDiscoveryUniverse): void {
  _setStrategyUniverse(universe.strategy ?? []);
  _setPortfolioUniverse(universe.portfolio ?? []);
}

function setEquityUniverseMaps(equities: readonly DSBStrategyItemResponseDTO[]): void {
  const byCode: [string, DSBStrategyItemResponseDTO][] = [];
  const byId: [string, DSBStrategyItemResponseDTO][] = [];

  for (const equity of equities) {
    byCode.push([equity.code, equity]);
    byId.push([equity.id, equity]);
  }

  allEquitiesByCode.value = new Map(byCode);
  allEquitiesById.value = new Map(byId);
}

const trackSearchToolbarHeight = ref(0);

/**
 * This store was implemented in order to avoid the performance issues introduced by using `useAllStrategiesByCode`
 * and related `useDataDiscovery` composable functions inside the `useBenchmark` composable.
 *
 * We call `useBenchmark` extensively throughout the application and every time it was called using the `useDataDiscovery`
 * composable, it would create a performance bottleneck.
 *
 * `allStrategies*` do NOT include stocks. Prefer `allTracksByCode` / `allTracksById` when resolving
 * benchmarks or other tracks that may be equities.
 */
export function useDiscoverStore(): {
  /**
   * Does NOT include stocks
   */
  allStrategies: Ref<readonly DSBStrategyItemResponseDTO[]>;
  /**
   * Does NOT include stocks. Prefer `allTracksByCode` for benchmark / track resolution.
   */
  allStrategiesByCode: Ref<Map<string, DSBStrategyItemResponseDTO>>;
  /**
   * Does NOT include stocks. Prefer `allTracksById` for benchmark / track resolution.
   */
  allStrategiesById: Ref<Map<string, DSBStrategyItemResponseDTO>>;
  /**
   * Strategies + equities by code (lookup facade — supports get/has).
   */
  allTracksByCode: ComputedRef<StrategyLookupMap>;
  /**
   * Strategies + equities by id (lookup facade — supports get/has).
   */
  allTracksById: ComputedRef<StrategyLookupMap>;
  allPortfolios: Ref<readonly DSBPortfolioItemResponseDTO[]>;
  allPortfoliosById: Ref<Map<string, DSBPortfolioItemResponseDTO>>;
  allPortfoliosBySlug: Ref<Map<string, DSBPortfolioItemResponseDTO>>;
  trackSearchToolbarHeight: Ref<number>;
  setUniverse: (universe: DataDiscoveryUniverse) => void;
  setEquityUniverseMaps: (equities: readonly DSBStrategyItemResponseDTO[]) => void;
} {
  return {
    allStrategies,
    allStrategiesByCode,
    allStrategiesById,
    allTracksByCode,
    allTracksById,

    allPortfolios,
    allPortfoliosById,
    allPortfoliosBySlug,

    trackSearchToolbarHeight,

    setUniverse,
    setEquityUniverseMaps,
  };
}
